import { post } from "../../../../../axios-interceptor-setup/apiService";
import { switchesFromFilter } from "./deviceStates";

const BASE_URL = "/smart_city_tcu";

// Filter (same endpoints as Device Health)
export const getLastRefreshDate = () => {
    return post(`${BASE_URL}/lastRefreshDateDeviceHealth`, {});
}

export const getCityDepotContract = (body) => {
    return post(`${BASE_URL}/locationFilter`, {...body});
}


// CSV downloads (body = GlobalFilter): posts to a download api and saves the file as `<fileName>.csv`
export const LATENCY_DOWNLOAD_APIS = {
    deviceHealth: "downloadLatencyDeviceHealthVehicleWise",
    latencyTrend: "downloadLatencyTrend",
    layerLatency: "downloadLayerLatencyVehicleWise"
};

export const downloadCsv = (api, body, fileName) => {
    return post(`${BASE_URL}/${api}`, {...body}, { responseType: "blob" })
        .then((data) => {
            const url = window.URL.createObjectURL(data);

            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName}.csv`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        });
}


// Device Health card
// TODO: confirm endpoint names with backend (deviceStateStaticData.js is the stand-in)
// -> { data: [{ state, vehicleCount, actualInterval, cities: [{ City, vehicleCount }] }] }
//    actualInterval = avg sec today, null if not observed; cities = vehicles in that state per city
export const getDeviceStateSummary = (body) => {
    return post(`${BASE_URL}/latencyDeviceStateSummary`, {...body});
}

// body.state = state key, body.city / body.depot = [{ label, value }]
// -> { data: [{ vin, regNumber, City, Depot, actualInterval }] }
export const getDeviceStateVehicles = (body) => {
    return post(`${BASE_URL}/latencyDeviceStateVehicles`, {...body});
}

// Vehicles popover drill-down: city -> depot -> vehicles (body.state = state key)
// -> { data: [{ City, vehicleCount, actualInterval }] }
export const getDeviceStateCityWise = (body) => {
    return post(`${BASE_URL}/latencyDeviceStateCityWise`, {...body});
}

// body.city = [{ label, value }] -> { data: [{ City, Depot, vehicleCount, actualInterval }] }
export const getDeviceStateDepotWise = (body) => {
    return post(`${BASE_URL}/latencyDeviceStateDepotWise`, {...body});
}

// Latency trend card
// body = { search, startDate, endDate, city, depot, contract, graphType: "minute" | "hour" | "day",
//          hour: "00" … "23" (minute only) }
// API data: { "16:00": 20.34, "16:01": 19.99, ... }  (label -> avg sec, in time order)
// chart row: { label, avgInterval }   avgInterval in sec
const toLatencyTrendRows = (data) => {
    if (Array.isArray(data)) return data;
    return Object.entries(data || {}).map(([label, value]) => ({
        label,
        avgInterval: value === null || value === undefined || value === "" ? null : Number(value)
    }));
};

export const getLatencyTrend = (body) => {
    return post(`${BASE_URL}/latencyTrend`, {...body})
        .then(res => ({ ...res, data: toLatencyTrendRows(res?.data) }));
}

// Device Health state table: city -> depot -> vehicles (popover)
// body = GlobalFilter: { search, startDate, endDate, city, depot, contract,
//                        ignitionOn (bool), emergencyStatus (0/1), backupBatteryStatus (0/1) = Mains }
// API row: { City, Depot_Name?, Total_Vehicles, Distinct_Vehicles, Active_Bus_Count, avg_l3_l1_lag }
// table row: { City, Depot?, totalVehicles, activeFleet, actualCount, actualInterval }
//    actualCount = active vehicles currently in that combination
//    actualInterval = average L3-L1 lag (sec) of those vehicles, null if none reported
const toDeviceStateRow = (row) => ({
    City: row.City,
    ...(row.Depot_Name !== undefined ? { Depot: row.Depot_Name } : {}),
    totalVehicles: row.Total_Vehicles,
    activeFleet: row.Distinct_Vehicles,
    actualCount: row.Active_Bus_Count,
    actualInterval: row.avg_l3_l1_lag ?? null
});

export const getDeviceStateCityTable = (body) => {
    return post(`${BASE_URL}/latencyDeviceHealthCityWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toDeviceStateRow) }));
}

// body.city = [{ label, value }] (the opened city) -> same row shape as city-wise, plus Depot
export const getDeviceStateDepotTable = (body) => {
    return post(`${BASE_URL}/latencyDeviceHealthDepotWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toDeviceStateRow) }));
}

// body.city / body.depot = [{ label, value }] (the clicked depot)
// popover row: { vin, regNumber, City, Depot, mains, ignition, emergency, actualInterval }
// TODO: confirm the vehicle row keys with backend; the API returns vehicles of the requested
//       combination, so the switches fall back to the body when a row does not carry them
const toDeviceStateVehicle = (body) => (row) => {
    const requested = switchesFromFilter(body);
    const has = (key) => row[key] !== undefined && row[key] !== null;

    return {
        vin: row.VIN ?? row.Vin ?? row.vin,
        regNumber: row.RegNumber,
        City: row.City,
        Depot: row.Depot_Name ?? row.Depot,
        mains: has("backupBatteryStatus") ? Number(row.backupBatteryStatus) === 1 : requested.mains,
        ignition: has("ignitionOn") ? !!row.ignitionOn : requested.ignition,
        emergency: has("emergencyStatus") ? Number(row.emergencyStatus) === 1 : requested.emergency,
        actualInterval: row.l3_l1_delay ?? null
    };
};

export const getDeviceStateComboVehicles = (body) => {
    return post(`${BASE_URL}/latencyDeviceHealthVehicleWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toDeviceStateVehicle(body)) }));
}

// Layer-wise latency breakdown: city -> depot -> VIN
// body = GlobalFilter: { search, startDate, endDate, city, depot, contract, ... }
// row: { City, Depot?, vin?, regNumber?, activeVin, actualPct, overallPct, "0-5", "5-10", "10-20", "20-30", "30-45", "45-60", ">60" }
//   activeVin  = no. of active VINs (VIN rows: 1 active / 0 inactive)
//   actualPct  = % of active vehicles out of all vehicles
//   overallPct = % of messages received within 30 seconds
//   buckets    = no. of VINs per avg-latency bucket (VIN rows: no. of packets)
// API row: { City, Total_Vehicles, Active_Vehicles, active_vehicle_percentage, messages_0_30,
//            messages_30_60, messages_60_90, messages_gt_90, total_messages, percentage_0_30 }
const toLayerLatencyRow = (row) => ({
    City: row.City,
    ...(row.Depot_Name !== undefined ? { Depot: row.Depot_Name } : {}),
    activeVin: row.Active_Vehicles,
    actualPct: row.active_vehicle_percentage ?? null,
    overallPct: row.percentage_0_30 ?? null,
    "0-5": row.messages_0_5,
    "5-10": row.messages_5_10,
    "10-20": row.messages_10_20,
    "20-30": row.messages_20_30,
    "30-45": row.messages_30_45,
    "45-60": row.messages_45_60,
    ">60": row.messages_gt_60,
});

export const getLayerLatencyCityWise = (body) => {
    return post(`${BASE_URL}/layerLatencyCityWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toLayerLatencyRow) }));
}

// body.city = [{ label, value }] (the opened city) -> same row shape as city-wise, plus Depot_Name
export const getLayerLatencyDepotWise = (body) => {
    return post(`${BASE_URL}/layerLatencyDepotWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toLayerLatencyRow) }));
}

// body.city / body.depot = [{ label, value }] (the clicked depot)
// API row: { vin, RegNumber, City, Depot_Name, Contract, status: "Active" | ..., messages_0_30,
//            messages_30_60, messages_60_90, messages_gt_90, total_messages, percentage_0_30 }
const toLayerLatencyVehicle = (row) => ({
    ...toLayerLatencyRow(row),
    vin: row.vin,
    regNumber: row.RegNumber,
    activeVin: String(row.status).toLowerCase() === "active" ? 1 : 0
});

export const getLayerLatencyVehicleWise = (body) => {
    return post(`${BASE_URL}/layerLatencyVehicleWise`, {...body})
        .then(res => ({ ...res, data: (res?.data || []).map(toLayerLatencyVehicle) }));
}
